import {
  FORM_BOR_TOR_46,
  FORM_NOR_JOR_2,
  FORM_POWER_OF_ATTORNEY,
  NOR_JOR_2_JOB_ROWS,
  resolveJobRow,
  thaiFormDate,
  thaiNumeral,
} from "./mouPrintForms.js";

/**
 * P164-R5: the printable government forms themselves, laid out for A4.
 *
 * Each sheet is a re-typeset of a real page out of the owner's own
 * แบบฟอร์ม MOU MYANMAR.pdf, not a picture of it: the PDF's pages are scans,
 * and a scan cannot have this round's employer name printed into it. Both the
 * Thai and the English line are kept on every field, because that is how the
 * real forms read and an office checks both halves.
 *
 * THE RULE THIS FILE FOLLOWS. A field the round holds is printed. A field it
 * does not hold is a dotted blank, the same width the paper form gives it. The
 * system never invents an address, a wage or a licence number to make a page
 * look finished - a form filed with an invented figure on it is a real problem
 * at a real counter, and every blank here is one somebody was always going to
 * fill in by hand anyway.
 */

/**
 * One field. With a value it prints the value over the rule; without one it
 * prints the rule alone, exactly as the paper form does.
 *
 * `size` is in characters, so a blank keeps the width the government form gives
 * that field rather than collapsing to nothing when it is empty.
 */
function Fill({ value, size = 20 }) {
  const text = value == null || value === "" ? null : String(value);
  return (
    <span className={`wx-mouprint-fill ${text ? "wx-mouprint-fill--filled" : ""}`} style={{ minWidth: `${size}ch` }}>
      {text ?? ""}
    </span>
  );
}

/** A tick-box. Ticked only from a fact; never on a guess. */
function Tick({ checked }) {
  return <span className={`wx-mouprint-tick ${checked ? "wx-mouprint-tick--on" : ""}`} aria-hidden="true" />;
}

/** One bilingual row: the Thai line and the English line that sits beside it. */
function Row({ th, en }) {
  return (
    <div className="wx-mouprint-row">
      <div className="wx-mouprint-th">{th}</div>
      <div className="wx-mouprint-en">{en}</div>
    </div>
  );
}

function SheetHead({ code, title, englishTitle, note }) {
  return (
    <header className="wx-mouprint-sheet-head">
      {code && <p className="wx-mouprint-formcode">{code}</p>}
      <h2 className="wx-mouprint-sheet-title">{title}</h2>
      <p className="wx-mouprint-sheet-en">{englishTitle}</p>
      {note}
    </header>
  );
}

/**
 * A quiet line at the foot of every sheet naming the round it was produced
 * from. It is how a page that has left the building is traced back to its
 * record, and it is the reason nobody has to guess which of a company's twenty
 * rounds a loose printout belongs to.
 */
function SheetFoot({ round }) {
  return (
    <footer className="wx-mouprint-sheet-foot">
      <span>
        {round.plcName} · {round.plcCode}
      </span>
      <span>
        รอบที่ {round.roundNumber} · {round.caseCode} · {round.countryLabel}
      </span>
    </footer>
  );
}

/**
 * แบบ นจ. ๒ - คำร้องขอนำคนต่างด้าวมาทำงานกับนายจ้างในประเทศ (page 1 of the
 * owner's PDF). The form step 1 of the process is filed on, and the one this
 * round holds the most real data for.
 */
function NorJor2Sheet({ round, today }) {
  const job = resolveJobRow(round.jobTypeLabel);
  const date = thaiFormDate(today);

  return (
    <article className="wx-mouprint-sheet">
      <SheetHead
        code="แบบ นจ. ๒"
        title="คำร้องขอนำคนต่างด้าวมาทำงานกับนายจ้างในประเทศ"
        englishTitle="Application Form for bringing foreign worker to work with employer in the Kingdom of Thailand"
        note={
          <p className="wx-mouprint-nationality">
            (สัญชาติ <Fill value={round.countryLabel} size={16} />) (Nationality <Fill value={round.countryEnglishLabel} size={16} />)
          </p>
        }
      />

      <Row
        th={
          <>
            <b>{thaiNumeral(1)}. ข้าพเจ้า (นายจ้าง/สถานประกอบการ)</b> <Fill value={round.employerName} size={30} />
          </>
        }
        en={
          <>
            <b>1. (Employer/Company)</b> <Fill value={round.employerName} size={30} />
          </>
        }
      />
      <Row
        th={<>ประเภทธุรกิจ/กิจการ <Fill size={28} /></>}
        en={<>Type of business <Fill size={28} /></>}
      />
      <Row
        th={<>เลขที่ <Fill size={6} /> หมู่ที่ <Fill size={4} /> ตรอก/ซอย <Fill size={14} /></>}
        en={<>Address No. <Fill size={6} /> Moo <Fill size={4} /> Soi <Fill size={14} /></>}
      />
      <Row
        th={<>ถนน <Fill size={13} /> แขวง/ตำบล <Fill size={14} /></>}
        en={<>Road <Fill size={13} /> Sub-District <Fill size={14} /></>}
      />
      <Row
        th={<>เขต/อำเภอ <Fill size={12} /> จังหวัด <Fill size={14} /></>}
        en={<>District <Fill size={12} /> Province <Fill size={14} /></>}
      />
      <Row
        th={<>โทรศัพท์ <Fill size={12} /> โทรสาร <Fill size={12} /></>}
        en={<>Telephone <Fill size={12} /> Fax <Fill size={12} /></>}
      />

      <Row
        th={
          <>
            <b>{thaiNumeral(2)}. แต่งตั้งให้บริษัทประเทศต้นทาง ชื่อ</b> <Fill value={round.originCompanyName} size={28} />
          </>
        }
        en={
          <>
            <b>2. The name of authorized company from the country of origin</b> <Fill value={round.originCompanyName} size={28} />
          </>
        }
      />
      <Row th={<>ที่อยู่ <Fill size={34} /></>} en={<>Address <Fill size={34} /></>} />
      <Row
        th={<>ตามหนังสือแต่งตั้งลงวันที่ <Fill size={5} /> / <Fill size={9} /> / <Fill size={5} /></>}
        en={<>Concerning the Power of Attorney on (date) <Fill size={5} /> / <Fill size={9} /> / <Fill size={5} /></>}
      />

      <Row
        th={
          <>
            <b>{thaiNumeral(3)}. ประสงค์จะจ้างคนต่างด้าว จำนวน</b> <Fill value={round.workerCountRequested} size={8} /> คน
          </>
        }
        en={
          <>
            <b>3. We would like to hire</b> <Fill value={round.workerCountRequested} size={8} /> (number of) foreign worker (s).
          </>
        }
      />

      {/* The form's own four job rows. Whichever the round's free-text job type
          resolves to is ticked; the head-count split by sex is left blank
          because the round records a total only, and splitting it would be an
          invention on a government form. */}
      {NOR_JOR_2_JOB_ROWS.map((row) => (
        <div className="wx-mouprint-row" key={row.key}>
          <div className="wx-mouprint-th">
            <Tick checked={job.key === row.key} /> {row.thai}
            <span className="wx-mouprint-sub">
              เพศชาย จำนวน <Fill size={5} /> คน เพศหญิง จำนวน <Fill size={5} /> คน
            </span>
          </div>
          <div className="wx-mouprint-en">
            {row.english}
            <span className="wx-mouprint-sub">
              Male <Fill size={6} /> person (s) Female <Fill size={6} /> person (s)
            </span>
          </div>
        </div>
      ))}
      <div className="wx-mouprint-row">
        <div className="wx-mouprint-th">
          <Tick checked={job.key === "other"} /> งานอื่นๆ (ระบุ) <Fill value={job.otherText} size={16} />
          <span className="wx-mouprint-sub">
            เพศชาย จำนวน <Fill size={5} /> คน เพศหญิง จำนวน <Fill size={5} /> คน
          </span>
        </div>
        <div className="wx-mouprint-en">
          Other (specify) <Fill value={job.otherText} size={16} />
          <span className="wx-mouprint-sub">
            Male <Fill size={6} /> person (s) Female <Fill size={6} /> person (s)
          </span>
        </div>
      </div>

      <Row
        th={<><b>{thaiNumeral(4)}. คุณสมบัติที่ต้องการ :</b> อายุ <Fill size={4} /> ปี ส่วนสูง <Fill size={5} /> น้ำหนัก <Fill size={5} /></>}
        en={<><b>4. Qualification :</b> Age <Fill size={4} /> year (s) Height <Fill size={5} /> Weight <Fill size={5} /></>}
      />
      <Row
        th={<><b>{thaiNumeral(5)}. สถานที่ทำงานของคนต่างด้าวในประเทศไทย</b></>}
        en={<><b>5. Place of work of foreign worker in Thailand</b></>}
      />
      <Row
        th={<>เลขที่ <Fill size={6} /> หมู่ที่ <Fill size={4} /> ตรอก/ซอย <Fill size={14} /></>}
        en={<>Address No. <Fill size={6} /> Moo <Fill size={4} /> Soi <Fill size={14} /></>}
      />
      <Row
        th={<>ถนน <Fill size={13} /> แขวง/ตำบล <Fill size={14} /></>}
        en={<>Road <Fill size={13} /> Sub-District <Fill size={14} /></>}
      />
      <Row
        th={<>เขต/อำเภอ <Fill size={12} /> จังหวัด <Fill size={14} /></>}
        en={<>District <Fill size={12} /> Province <Fill size={14} /></>}
      />
      <Row
        th={<><b>{thaiNumeral(6)}. ระยะเวลาการจ้าง</b> <Fill size={5} /> ปี <Fill size={5} /> เดือน</>}
        en={<><b>6. Period of employment</b> <Fill size={5} /> year (s) <Fill size={5} /> month (s)</>}
      />
      <Row
        th={<><b>{thaiNumeral(7)}. ค่าจ้าง</b> <Fill size={10} /> บาท ต่อวัน/เดือน</>}
        en={<><b>7. Wage</b> <Fill size={10} /> baht per day/month</>}
      />
      <Row
        th={<><b>{thaiNumeral(8)}. ชั่วโมงการทำงานปกติ</b> <Fill size={6} /> ชั่วโมง</>}
        en={<><b>8. Number of normal working hours</b> <Fill size={6} /> hours.</>}
      />
      <Row
        th={<><b>{thaiNumeral(9)}. วันหยุดประจำสัปดาห์</b> <Fill size={4} /> วัน วันหยุดพักผ่อนประจำปี <Fill size={4} /> วัน วันหยุดตามประเพณี <Fill size={4} /> วัน</>}
        en={<><b>9. Weekly holiday</b> <Fill size={4} /> day (s) Annual leave <Fill size={4} /> day (s) Thai official holiday <Fill size={4} /> day (s)</>}
      />
      <Row
        th={<><b>{thaiNumeral(10)}. คนต่างด้าวจะเดินทางเข้ามาผ่านด่านตรวจคนเข้าเมือง</b> <Fill size={18} /></>}
        en={<><b>10. The Immigration checkpoint which foreign worker will enter</b> <Fill size={18} /></>}
      />
      <Row
        th={<><b>{thaiNumeral(11)}. คนต่างด้าวเข้ารับการอบรมที่ศูนย์แรกรับเข้าทำงานและสิ้นสุดการจ้าง จังหวัด</b> <Fill size={16} /></>}
        en={<><b>11. Foreign Worker received training at Post-Arrival and Repatriation Center,</b> <Fill size={16} /> Province</>}
      />

      <Row
        th={<p className="wx-mouprint-declare">ทั้งนี้ข้าพเจ้าขอรับรองว่าจะดำเนินการให้คนต่างด้าวที่ขอนำมาทำงานปฏิบัติตนให้ถูกต้องตามกฎหมายทุกประการ</p>}
        en={<p className="wx-mouprint-declare">We hereby accept that all processes of bringing foreign workers to work shall comply with the Thai laws and regulations.</p>}
      />

      <div className="wx-mouprint-signblock">
        <div className="wx-mouprint-sign">
          <span className="wx-mouprint-signline" />
          <span>ลายมือชื่อ / Signature</span>
        </div>
        <div className="wx-mouprint-sign">
          <span>
            วันที่ <Fill value={date.day} size={4} /> เดือน <Fill value={date.month} size={10} /> พ.ศ. <Fill value={date.year} size={6} />
          </span>
          <span>Date / Month / Year</span>
        </div>
      </div>

      <SheetFoot round={round} />
    </article>
  );
}

/**
 * หนังสือมอบอำนาจ / Power of Attorney (page 9 of the owner's PDF).
 *
 * The one form whose body text names the country and the head count in a
 * sentence rather than in a box - which is exactly the wording the owner meant
 * by "เอกสารจะเปลี่ยนไปตามประเทศนั้นๆ".
 */
function PowerOfAttorneySheet({ round, today }) {
  const date = thaiFormDate(today);

  return (
    <article className="wx-mouprint-sheet">
      <SheetHead code="หนังสือมอบอำนาจ" title="หนังสือมอบอำนาจ" englishTitle="Power of Attorney" />

      <div className="wx-mouprint-poa-top">
        <Row
          th={<>ทำที่ <Fill value={round.plcName} size={26} /></>}
          en={<>Written at <Fill value={round.plcName} size={26} /></>}
        />
        <Row
          th={<>วันที่ <Fill value={date.day} size={4} /> เดือน <Fill value={date.month} size={10} /> พ.ศ. <Fill value={date.year} size={6} /></>}
          en={<>Date <Fill value={date.day} size={4} /> Month <Fill value={date.month} size={10} /> B.E. <Fill value={date.year} size={6} /></>}
        />
      </div>

      <Row
        th={<>ข้าพเจ้า นาย/นาง/นางสาว <Fill size={34} /></>}
        en={<>Mr. /Mrs./Miss <Fill size={34} /></>}
      />
      <Row
        th={<>ขอมอบอำนาจให้ นาย/นาง/นางสาว <Fill size={30} /></>}
        en={<>Hereby authorize and appoint Mr. /Mrs./Miss <Fill size={30} /></>}
      />
      <Row
        th={<>ปัจจุบันทำงานในตำแหน่ง <Fill size={30} /></>}
        en={<>At present working in the position of <Fill size={30} /></>}
      />
      <Row
        th={<>ตั้งอยู่ที่สำนักงานชื่อ <Fill value={round.plcName} size={24} /> โทร. <Fill size={12} /></>}
        en={<>at the office of <Fill value={round.plcName} size={24} /> Tel. <Fill size={12} /></>}
      />
      <Row
        th={<>ตั้งอยู่เลขที่ <Fill size={10} /> ซอย <Fill size={12} /> ถนน <Fill size={12} /></>}
        en={<>Located on <Fill size={10} /> Soi/Lane <Fill size={12} /> Road <Fill size={12} /></>}
      />
      <Row
        th={<>แขวง/ตำบล <Fill size={12} /> เขต/อำเภอ <Fill size={12} /> จังหวัด <Fill size={12} /></>}
        en={<>Sub-District <Fill size={12} /> District <Fill size={12} /> Province <Fill size={12} /></>}
      />

      <Row
        th={
          <p className="wx-mouprint-declare">
            มีอำนาจดำเนินการเกี่ยวกับการนำเข้าแรงงานต่างด้าวสัญชาติ <Fill value={round.countryLabel} size={12} /> จำนวน{" "}
            <Fill value={round.workerCountRequested} size={6} /> คน ตามบันทึกความเข้าใจ (MOU) และลงนามในเอกสารประกอบการนำเข้าแรงงาน
            แทนข้าพเจ้าได้ทุกฉบับ รวมทั้งเปลี่ยนแปลงแก้ไขข้อความเอกสารดังกล่าวด้วย
          </p>
        }
        en={
          <p className="wx-mouprint-declare">
            to be lawful and legal attorney for the purpose of recruiting <Fill value={round.workerCountRequested} size={6} />{" "}
            <Fill value={round.countryEnglishLabel} size={12} /> workers in concerning with Memorandum of Understanding (MOU) and sign
            any documents on behalf of myself Including Changing words on the related documents.
          </p>
        }
      />

      <Row
        th={
          <p className="wx-mouprint-declare">
            การใดที่ นาย/นาง/นางสาว <Fill size={24} /> ได้กระทำไปให้ถือเสมือนว่าข้าพเจ้าได้กระทำเองทุกประการ
          </p>
        }
        en={
          <p className="wx-mouprint-declare">
            What has been done by <Fill size={24} /> will remain in full force and effect as it has been done by myself
          </p>
        }
      />

      <div className="wx-mouprint-signgrid">
        {[
          ["ผู้มอบอำนาจ", "Grantor"],
          ["ผู้รับมอบอำนาจ", "Grantee"],
          ["พยาน", "Witness"],
          ["พยาน", "Witness"],
        ].map(([thai, english], index) => (
          <div className="wx-mouprint-sign" key={`${english}-${index}`}>
            <span className="wx-mouprint-signline" />
            <span>
              ลงชื่อ / Signed — {thai} / {english}
            </span>
            <span className="wx-mouprint-signname">( ..................................................... )</span>
          </div>
        ))}
      </div>

      <p className="wx-mouprint-remark">
        หมายเหตุ หากผู้มอบอำนาจประสงค์จะจำกัดขอบการมอบอำนาจเป็นอย่างอื่น ย่อมกระทำได้ โดยไม่ต้องใช้เนื้อความตามนี้
        <br />
        Remark In case grantor prefer to Limit the authorization giving to the grantee it could be done by using the other forms of
        power of attorney.
      </p>

      <SheetFoot round={round} />
    </article>
  );
}

/**
 * แบบ บต. ๔๖ - หนังสือรับรองการจ้าง / Form WP. 46 (page 10 of the owner's PDF).
 *
 * The form a round holds the LEAST of: almost every field on it is about the
 * employer's finances and the individual worker, neither of which this system
 * records. It is still printed, because the operator needs the sheet in the
 * pack and a blank one straight off the round beats hunting for the file - but
 * nothing on it is filled from a guess.
 */
function BorTor46Sheet({ round, today }) {
  const date = thaiFormDate(today);

  return (
    <article className="wx-mouprint-sheet">
      <SheetHead code="แบบ บต. ๔๖ / Form WP. 46" title="หนังสือรับรองการจ้าง" englishTitle="EMPLOYMENT CERTIFICATION" />

      <h3 className="wx-mouprint-section">๑. ข้อมูลนายจ้าง / Particulars of employer</h3>
      <Row
        th={<><Tick /> นิติบุคคลไทย จดทะเบียนเมื่อ <Fill size={10} /> เลขที่ <Fill size={10} /> ทุนจดทะเบียนชำระแล้ว <Fill size={10} /> บาท</>}
        en={<>Thai juristic person registered on / No. / Paid-up capital THB</>}
      />
      <Row
        th={<><Tick /> นิติบุคคลต่างด้าว จดทะเบียนเมื่อ <Fill size={10} /> จำนวนเงินที่นำเข้ามาจากต่างประเทศ <Fill size={10} /> บาท</>}
        en={<>Foreign juristic person registered on / Amount of money imported from abroad THB</>}
      />
      <Row
        th={<><Tick /> บุคคลธรรมดา บัตรประจำตัวประชาชนเลขที่ <Fill size={14} /> ใบอนุญาตทำงานเลขที่ <Fill size={12} /></>}
        en={<>Natural person / National identification card No. / Work permit No.</>}
      />
      <Row
        th={<>ชื่อนายจ้าง/สถานประกอบการ <Fill value={round.employerName} size={30} /></>}
        en={<>Name of employer <Fill value={round.employerName} size={30} /></>}
      />
      <Row th={<>ที่ตั้งสถานประกอบการ <Fill size={34} /></>} en={<>Address <Fill size={34} /></>} />
      <Row th={<>ประเภทกิจการ <Fill size={30} /></>} en={<>Type of business <Fill size={30} /></>} />

      <h3 className="wx-mouprint-section">๒. ข้อมูลการจ้าง / Particulars of employment</h3>
      <Row
        th={<>ข้าพเจ้าประสงค์จะจ้างคนต่างด้าวชื่อ <Fill size={28} /></>}
        en={<>I wish to employ a foreigner named <Fill size={28} /></>}
      />
      <Row
        th={<>สัญชาติ <Fill value={round.countryLabel} size={14} /> หมู่โลหิต <Fill size={8} /></>}
        en={<>Nationality <Fill value={round.countryEnglishLabel} size={14} /> Blood type <Fill size={8} /></>}
      />
      <Row th={<>ที่อยู่ในประเทศไทย <Fill size={32} /></>} en={<>Address in Thailand <Fill size={32} /></>} />
      <Row
        th={<>ประเภทงาน <Fill value={round.jobTypeLabel} size={26} /></>}
        en={<>Type(s) of work <Fill value={round.jobTypeLabel} size={26} /></>}
      />
      <Row th={<>ลักษณะงาน <Fill size={32} /></>} en={<>Nature of work <Fill size={32} /></>} />
      <Row th={<>สถานที่ทำงานของคนต่างด้าว <Fill size={28} /></>} en={<>Place of work of the foreigner <Fill size={28} /></>} />
      <Row
        th={<>ระยะเวลาการจ้าง <Fill size={5} /> ปี <Fill size={5} /> เดือน <Fill size={5} /> วัน มีสัญญาจ้างถึงวันที่ <Fill size={10} /></>}
        en={<>Period of employment Year(s) / Month(s) / Day(s) — Employment contract is valid until</>}
      />
      <Row
        th={<>ค่าจ้างหรือรายได้ วันละ / เดือนละ <Fill size={10} /> บาท ผลประโยชน์อื่น วันละ / เดือนละ <Fill size={10} /> บาท</>}
        en={<>Wage or income per day / per month THB — Other benefits per day / per month THB</>}
      />

      <h3 className="wx-mouprint-section">๓. เหตุผลที่ไม่จ้างบุคคลสัญชาติไทยเข้าทำงาน / Reason(s) for not employing a Thai national</h3>
      <p className="wx-mouprint-lines">
        <Fill size={80} />
        <Fill size={80} />
      </p>

      <Row
        th={<p className="wx-mouprint-declare">ข้าพเจ้าขอรับรองว่า ข้อความข้างต้นนี้เป็นความจริงทุกประการ</p>}
        en={
          <p className="wx-mouprint-declare">
            I hereby certify that all particulars given in this form are true and correct to the best of my knowledge and belief.
          </p>
        }
      />

      <div className="wx-mouprint-signblock">
        <div className="wx-mouprint-sign">
          <span className="wx-mouprint-signline" />
          <span>ลงชื่อ นายจ้าง / Signature Employer</span>
          <span className="wx-mouprint-signname">( ..................................................... )</span>
        </div>
        <div className="wx-mouprint-sign">
          <span>ตำแหน่ง Title <Fill size={16} /></span>
          <span>
            ลงวันที่ Date <Fill value={`${date.day} ${date.month} ${date.year}`} size={20} />
          </span>
        </div>
      </div>

      <p className="wx-mouprint-remark">
        หมายเหตุ : ผู้ทำหนังสือรับรองนี้ จะต้องเป็นผู้มีอำนาจลงชื่อผูกพันสถานประกอบการ หรือได้รับมอบอำนาจให้ทำการแทน
        <br />
        THE PERSON WHO SIGNS THIS FORM MUST BE AN AUTHORIZED SIGNATORY OF THE COMPANY OR AN AUTHORIZED REPRESENTATIVE OF THE COMPANY
      </p>

      <SheetFoot round={round} />
    </article>
  );
}

const SHEETS = {
  [FORM_NOR_JOR_2]: NorJor2Sheet,
  [FORM_POWER_OF_ATTORNEY]: PowerOfAttorneySheet,
  [FORM_BOR_TOR_46]: BorTor46Sheet,
};

/** One form's sheet, or nothing at all for a key with no layout behind it. */
export default function MouPrintSheet({ formKey, round, today }) {
  const Sheet = SHEETS[formKey];
  return Sheet ? <Sheet round={round} today={today} /> : null;
}
